import { gsap, Power3, Power2 } from "gsap";
import BezierEasing from 'bezier-easing';

import { ControllerPage } from '../_app/cuchillo/pages/ControllerPage';
import Default from './Default';
import Header from '../layout/Header';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import NoScroll from './NoScroll';
import InfiniteScroll from '../_app/cuchillo/scroll/InfiniteScroll';
import InfiniteScroll__Item from '../_app/cuchillo/scroll/InfiniteScroll__Item';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Maths } from "../_app/cuchillo/utils/Maths";
import { Basics, isMobile, isSafari, isSmartphone } from "../_app/cuchillo/core/Basics";

export default class Home extends NoScroll {
  sliderDom;
  scroller;

  constructor() {
    super();

    this.sliderDom = GetBy.class('__slider', this.container)[0];

    this.scroller = new InfiniteScroll(InfiniteScroll.AXIS_Y, {
      domResize: this.sliderDom,
      container: this.sliderDom,
      multiplicator: 1.3,
      itemClass: GallerySlider__Item,
      minSpeed: .2,
      inverted: false,
      gap: 0,
      hasVirtuaScroll: true
    });

    if (!isSmartphone) this.scroller.directGoto(6);
  }

  afterShow() {
    this._isActive = true;

    Scroll.show();

    if (!this.isFirstTime) Header.showLogo();
    else if (isMobile) Header.hideLogo();

    this.scroller.show();
    this.scroller.start();

    if (!isSmartphone) this.scroller.goto(2);
  }

  beforeHide() {
    super.beforeHide();
    this.scroller.hide();
    this.scroller.stop();
  }

  afterHide() {
    super.afterHide();
    Header.hideLogo();
  }

  loop() {
    super.loop();
    this.scroller.loop();
  }

  dispose() {
    this.scroller.dispose();
    super.dispose();
  }

  resize(__w, __h) {
    super.resize(__w, __h);
    this.scroller.resize();
  }
}

ControllerPage._addPage('home', Home);

class GallerySlider__Item extends InfiniteScroll__Item {
  easing = BezierEasing(0.69, 0.27, 0.69, 0.28);
  figure = GetBy.selector('figure', this.dom)[0];
  image = GetBy.selector('img', this.dom)[0];
  imgWrapper = GetBy.class('img', this.dom)[0];
  wrapper = GetBy.class('wrapper', this.dom)[0];
  overlay = GetBy.class('overlay', this.dom)[0];
  isShown = false;
  bgImage;

  _call = {
    hoverIn: () => {
      if (!this.isShown) return;

      gsap.killTweensOf(this.overlay);

      gsap.to(this.overlay, { height: '100%', duration: .6, ease: Power2.easeOut });
      // gsap.to(this.image, { scale: 1.2, duration: .6, ease: Power2.easeOut });

      if (!isSmartphone) {
        gsap.killTweensOf(this.bgImage);
        if (!isSafari) gsap.to(this.bgImage, { filter: 'blur(0px)', duration: .6, ease: Power2.easeOut });
        gsap.to(this.bgImage, { opacity: 1, duration: .5, ease: Power2.easeOut });
      }
    },
    hoverOut: () => {
      if (!this.isShown) return;

      gsap.killTweensOf(this.overlay);

      gsap.to(this.overlay, { height: 0, duration: .3, ease: Power2.easeOut });
      // gsap.to(this.image, { scale: 1, duration: .3, ease: Power2.easeOut });
      if (!isSmartphone) {
        gsap.killTweensOf(this.bgImage);
        if (!isSafari) gsap.to(this.bgImage, { filter: 'blur(10px)', duration: .4, ease: Power2.easeOut });
        gsap.to(this.bgImage, { opacity: 0, duration: .5, ease: Power2.easeOut });
      }
    }
  }

  constructor(__d, __i, __axis = InfiniteScroll.AXIS_Y) {
    super(__d, __i, __axis, true);

    gsap.set(this.overlay, { height: 0 });
    gsap.set(this.imgWrapper, { height: 0 });
    gsap.set(this.figure, { scale: 1.5, filter: 'blur(10px)' });

    if (!isSmartphone) {
      this.bgImage = GetBy.selector(`.__bg-item[data-index="${this.dom.dataset.index}"] img`)[0];
      gsap.set(this.bgImage, { opacity: 0 });
      if (!isSafari) gsap.set(this.bgImage, { filter: 'blur(10px)' });
    }

    this.addEventListeners();
  }

  show() {
    super.show();

    gsap.killTweensOf(this.imgWrapper);
    gsap.to(this.imgWrapper, {
      height: '100%',
      duration: 1.4,
      ease: Power3.easeOut,
      delay: .15 * this.index,
      onComplete: () => {
        this.isShown = true;
      }
    });

    gsap.killTweensOf(this.figure);
    gsap.to(this.figure, { scale: 1, duration: 1.8, ease: Power2.easeOut, delay: .15 * this.index });
    gsap.to(this.figure, { filter: 'blur(0px)', duration: .5, ease: Power3.easeOut, delay: .15 * this.index });
  }

  hide() {
    super.hide();

    this.imgWrapper.style.top = 0;
    this.imgWrapper.style.bottom = 'auto';

    gsap.killTweensOf(this.imgWrapper);
    gsap.to(this.imgWrapper, {
      height: 0,
      duration: .6,
      ease: Power3.easeOut,
      onComplete: () => {
        this.isShown = false;
      }
    });

    gsap.killTweensOf(this.figure);
    gsap.to(this.figure, { scale: 1.5, filter: 'blur(10px)', duration: .6, ease: Power2.easeOut });
  }

  addEventListeners() {
    if (!isMobile) {
      this.dom.addEventListener(Basics.mouseOver, this._call.hoverIn);
      this.dom.addEventListener(Basics.mouseOut, this._call.hoverOut);
    }
  }

  removeEventListeners() {
    if (!isMobile) {
      this.dom.removeEventListener(Basics.mouseOver, this._call.hoverIn);
      this.dom.removeEventListener(Basics.mouseOut, this._call.hoverOut);
    }
  }

  drawHook() {
    this.move();
  }

  move() {
    if (isMobile) return;

    const p = this.progressInside - 0.5;
    const pEase = this.easing(this.progressInside - 0.5);

    const speed = 1 + Maths.normalize(80, 0, Math.abs(this.speed));
    const scale = Maths.clamp(Maths.lerp(1, 1.2 * speed, Math.abs(pEase)), 1, 1.25);
    const blur = Math.max(Maths.lerp(0, 2.6, Math.abs(p) - .1), 0);

    if (p < 0) {
      this.wrapper.style.transformOrigin = 'center bottom';
    } else {
      this.wrapper.style.transformOrigin = 'center top';
    }

    gsap.set(this.wrapper, { scale });
    gsap.set(this.imgWrapper, { scale });
    gsap.set(this.dom, { filter: `blur(${blur}px)` });
  }

  dispose() {
    super.dispose();

    this.removeEventListeners();
  }
}
